import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Package, Box, MessageSquare, Share2, Check, Edit, Trash2, LogOut, Plus, X } from "lucide-react";
import { api, fetchOrders, fetchProducts, updateOrderStatus, updateProduct } from "../lib/api";
import { formatINR } from "../lib/constants";
import { useAdminAuth } from "../context/AdminAuthContext";
import AdminLogin from "./AdminLogin";

const TABS = [
    { id: "orders", label: "Orders", icon: Package },
    { id: "products", label: "Products", icon: Box },
    { id: "testimonials", label: "Reviews", icon: MessageSquare },
    { id: "social", label: "Social", icon: Share2 },
];

export default function Admin() {
    const { isAuthed, logout, verifying } = useAdminAuth();
    const [tab, setTab] = useState("orders");

    if (verifying) return <div className="pt-32 text-center text-zinc-500">Verifying…</div>;
    if (!isAuthed) return <AdminLogin />;

    return (
        <div className="pt-28 md:pt-32 pb-20" data-testid="admin-page">
            <div className="mx-auto max-w-7xl px-6 md:px-12">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tight">Admin</h1>
                        <p className="text-zinc-500 text-sm uppercase tracking-widest">Manage your store</p>
                    </div>
                    <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-none border border-white/15 hover:bg-white hover:text-bg0 text-xs uppercase tracking-widest font-bold transition-colors">
                        <LogOut className="w-3.5 h-3.5" /> Logout
                    </button>
                </div>

                <div className="mt-8 flex flex-wrap gap-1 p-1 rounded-sm bg-bg1 border border-white/10 w-fit">
                    {TABS.map((t) => (
                        <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`}
                            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider font-bold transition-colors ${tab === t.id ? "bg-lime text-bg0" : "text-zinc-300 hover:text-white"}`}>
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                    {tab === "orders" && <OrdersPanel />}
                    {tab === "products" && <ProductsPanel />}
                    {tab === "testimonials" && <TestimonialsPanel />}
                    {tab === "social" && <SocialPanel />}
                </div>
            </div>
        </div>
    );
}

// ---------- ORDERS ----------
function OrdersPanel() {
    const qc = useQueryClient();
    const { data: orders = [], isLoading } = useQuery({ queryKey: ["orders"], queryFn: fetchOrders });
    const mut = useMutation({
        mutationFn: ({ orderNumber, status }) => updateOrderStatus(orderNumber, status),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["orders"] }); toast.success("Order updated"); },
    });
    if (isLoading) return <div className="text-zinc-500">Loading…</div>;
    if (orders.length === 0) return <Empty msg="No orders yet." />;

    return (
        <div className="overflow-x-auto rounded-sm border border-white/10 bg-bg1">
            <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-zinc-500 border-b border-white/10">
                    <tr><Th>Order #</Th><Th>Customer</Th><Th>Items</Th><Th>Total</Th><Th>Status</Th><Th>Action</Th></tr>
                </thead>
                <tbody>
                    {orders.map((o) => (
                        <tr key={o.order_number} className="border-b border-white/5" data-testid={`admin-order-row-${o.order_number}`}>
                            <td className="p-4 font-mono text-lime">{o.order_number}</td>
                            <td className="p-4"><div className="font-semibold">{o.address.full_name}</div><div className="text-xs text-zinc-500">{o.address.city} · {o.address.phone}</div></td>
                            <td className="p-4 text-zinc-400">{o.items.reduce((s, i) => s + i.quantity, 0)}</td>
                            <td className="p-4 font-bold">{formatINR(o.total)}</td>
                            <td className="p-4"><StatusPill status={o.status} /></td>
                            <td className="p-4">
                                {o.status === "placed" && (
                                    <button onClick={() => mut.mutate({ orderNumber: o.order_number, status: "fulfilled" })} data-testid={`admin-fulfill-${o.order_number}`} className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-bold text-lime hover:text-white">
                                        <Check className="w-3.5 h-3.5" /> Fulfill
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function StatusPill({ status }) {
    const cls = status === "fulfilled" ? "bg-lime/15 text-lime" : status === "cancelled" ? "bg-red-500/15 text-red-400" : "bg-gold/15 text-gold";
    return <span className={`text-xs uppercase tracking-widest font-bold px-2 py-1 rounded-full ${cls}`}>{status}</span>;
}

// ---------- PRODUCTS ----------
function ProductsPanel() {
    const qc = useQueryClient();
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState({ price: 0, stock: 0 });
    const mut = useMutation({
        mutationFn: ({ id, payload }) => updateProduct(id, payload),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["products"] }); toast.success("Product updated"); setEditing(null); },
    });

    return (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
                <div key={p.id} className="p-6 rounded-sm border border-white/10 bg-bg1">
                    <div className="font-display text-xl uppercase tracking-tight">{p.name}</div>
                    <div className="text-xs text-zinc-500">{p.slug}</div>
                    {editing === p.id ? (
                        <div className="mt-5 space-y-3">
                            <Field label="Price (₹)"><input type="number" value={draft.price} onChange={(e) => setDraft({ ...draft, price: parseFloat(e.target.value) })} data-testid={`admin-edit-price-${p.slug}`} className={inputCls} /></Field>
                            <Field label="Stock"><input type="number" value={draft.stock} onChange={(e) => setDraft({ ...draft, stock: parseInt(e.target.value, 10) })} data-testid={`admin-edit-stock-${p.slug}`} className={inputCls} /></Field>
                            <div className="flex gap-2">
                                <button onClick={() => mut.mutate({ id: p.id, payload: draft })} data-testid={`admin-save-${p.slug}`} className="btn-pill btn-primary !py-2 !px-4 !text-xs flex-1">Save</button>
                                <button onClick={() => setEditing(null)} className="btn-pill btn-secondary !py-2 !px-4 !text-xs flex-1">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div className="mt-5">
                            <Row label="Price" value={formatINR(p.price)} />
                            <Row label="Stock" value={p.stock} />
                            <button onClick={() => { setEditing(p.id); setDraft({ price: p.price, stock: p.stock }); }} data-testid={`admin-edit-${p.slug}`} className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-lime hover:text-white">
                                <Edit className="w-3.5 h-3.5" /> Edit
                            </button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

// ---------- TESTIMONIALS ----------
function TestimonialsPanel() {
    const qc = useQueryClient();
    const { authHeaders } = useAdminAuth();
    const { data: items = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const [showNew, setShowNew] = useState(false);
    const empty = { name: "", location: "", age: 0, rating: 5, title: "", quote: "", product_slug: "redrive-max", verified: true };
    const [draft, setDraft] = useState(empty);
    const createMut = useMutation({
        mutationFn: () => api.post("/admin/testimonials", draft, { headers: authHeaders() }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["testimonials"] }); toast.success("Review added"); setShowNew(false); setDraft(empty); },
    });
    const delMut = useMutation({
        mutationFn: (id) => api.delete(`/admin/testimonials/${id}`, { headers: authHeaders() }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["testimonials"] }); toast.success("Deleted"); },
    });

    return (
        <div>
            <button onClick={() => setShowNew((v) => !v)} data-testid="admin-review-new" className="btn-pill btn-primary mb-5">
                {showNew ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showNew ? "Cancel" : "New Review"}
            </button>
            {showNew && (
                <div className="p-6 rounded-sm bg-bg1 border border-white/10 mb-6 grid md:grid-cols-2 gap-4">
                    <Field label="Customer Name"><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="admin-review-name" className={inputCls} /></Field>
                    <Field label="Location"><input value={draft.location} onChange={(e) => setDraft({ ...draft, location: e.target.value })} className={inputCls} /></Field>
                    <Field label="Age"><input type="number" value={draft.age} onChange={(e) => setDraft({ ...draft, age: parseInt(e.target.value, 10) })} className={inputCls} /></Field>
                    <Field label="Rating (1-5)"><input type="number" min={1} max={5} value={draft.rating} onChange={(e) => setDraft({ ...draft, rating: parseInt(e.target.value, 10) })} className={inputCls} /></Field>
                    <Field label="Product">
                        <select value={draft.product_slug} onChange={(e) => setDraft({ ...draft, product_slug: e.target.value })} className={inputCls}>
                            {products.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Title"><input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} data-testid="admin-review-title" className={inputCls} /></Field>
                    <div className="md:col-span-2"><Field label="Review"><textarea rows={3} value={draft.quote} onChange={(e) => setDraft({ ...draft, quote: e.target.value })} data-testid="admin-review-quote" className={inputCls + " min-h-[90px]"} /></Field></div>
                    <div className="md:col-span-2">
                        <button onClick={() => createMut.mutate()} disabled={!draft.name || !draft.title || !draft.quote} data-testid="admin-review-publish" className="btn-pill btn-primary disabled:opacity-50">Add Review</button>
                    </div>
                </div>
            )}
            <div className="grid md:grid-cols-2 gap-3">
                {items.map((t) => (
                    <div key={t.id} className="p-5 rounded-sm border border-white/10 bg-bg1">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="font-semibold">{t.title}</div>
                                <div className="text-xs text-zinc-500 mt-1">{t.name}{t.age ? `, ${t.age}` : ""} · {t.location} · for {t.product_slug}</div>
                                <p className="text-sm text-zinc-300 mt-3 line-clamp-3">{t.quote}</p>
                            </div>
                            <button onClick={() => { if (window.confirm("Delete?")) delMut.mutate(t.id); }} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- SOCIAL ----------
function SocialPanel() {
    const qc = useQueryClient();
    const { authHeaders } = useAdminAuth();
    const { data: items = [] } = useQuery({ queryKey: ["social"], queryFn: async () => (await api.get("/social")).data });
    const { data: products = [] } = useQuery({ queryKey: ["products"], queryFn: fetchProducts });
    const [showNew, setShowNew] = useState(false);
    const empty = { handle: "", platform: "instagram", avatar: "", content: "", product_slug: "redrive-max", likes: 0 };
    const [draft, setDraft] = useState(empty);
    const createMut = useMutation({
        mutationFn: () => api.post("/admin/social", draft, { headers: authHeaders() }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["social"] }); toast.success("Social post added"); setShowNew(false); setDraft(empty); },
    });
    const delMut = useMutation({
        mutationFn: (id) => api.delete(`/admin/social/${id}`, { headers: authHeaders() }),
        onSuccess: () => { qc.invalidateQueries({ queryKey: ["social"] }); toast.success("Deleted"); },
    });

    return (
        <div>
            <button onClick={() => setShowNew((v) => !v)} data-testid="admin-social-new" className="btn-pill btn-primary mb-5">
                {showNew ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />} {showNew ? "Cancel" : "New Post"}
            </button>
            {showNew && (
                <div className="p-6 rounded-sm bg-bg1 border border-white/10 mb-6 grid md:grid-cols-2 gap-4">
                    <Field label="Handle (e.g. @arjun.fit)"><input value={draft.handle} onChange={(e) => setDraft({ ...draft, handle: e.target.value })} data-testid="admin-social-handle" className={inputCls} /></Field>
                    <Field label="Platform">
                        <select value={draft.platform} onChange={(e) => setDraft({ ...draft, platform: e.target.value })} className={inputCls}>
                            <option value="instagram">Instagram</option>
                            <option value="twitter">Twitter / X</option>
                            <option value="facebook">Facebook</option>
                        </select>
                    </Field>
                    <Field label="Avatar URL"><input value={draft.avatar} onChange={(e) => setDraft({ ...draft, avatar: e.target.value })} className={inputCls} /></Field>
                    <Field label="Product">
                        <select value={draft.product_slug} onChange={(e) => setDraft({ ...draft, product_slug: e.target.value })} className={inputCls}>
                            {products.map((p) => <option key={p.id} value={p.slug}>{p.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Likes"><input type="number" value={draft.likes} onChange={(e) => setDraft({ ...draft, likes: parseInt(e.target.value, 10) })} className={inputCls} /></Field>
                    <div className="md:col-span-2"><Field label="Content / Caption"><textarea rows={3} value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })} data-testid="admin-social-content" className={inputCls + " min-h-[80px]"} /></Field></div>
                    <div className="md:col-span-2">
                        <button onClick={() => createMut.mutate()} disabled={!draft.handle || !draft.content} data-testid="admin-social-publish" className="btn-pill btn-primary disabled:opacity-50">Publish</button>
                    </div>
                </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((s) => (
                    <div key={s.id} className="p-4 rounded-sm border border-white/10 bg-bg1">
                        <div className="flex items-center gap-3">
                            {s.avatar && <img src={s.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />}
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold text-sm truncate">{s.handle}</div>
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{s.platform}</div>
                            </div>
                            <button onClick={() => { if (window.confirm("Delete?")) delMut.mutate(s.id); }} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <p className="mt-3 text-sm text-zinc-300 line-clamp-3">{s.content}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------- Helpers ----------
const inputCls = "w-full bg-bg0 border border-white/10 rounded-sm px-3 py-2 focus:border-lime outline-none placeholder:text-zinc-600";
function Field({ label, children }) { return <label className="block"><span className="text-xs uppercase tracking-widest text-zinc-500 block mb-1.5">{label}</span>{children}</label>; }
function Row({ label, value }) { return <div className="flex justify-between text-sm py-2 border-b border-white/5"><span className="text-zinc-500">{label}</span><span className="font-bold">{value}</span></div>; }
function Th({ children }) { return <th className="text-left p-4">{children}</th>; }
function Empty({ msg }) { return <div className="text-zinc-500 p-10 text-center bg-bg1 rounded-sm border border-white/10">{msg}</div>; }
function slugify(s) { return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""); }
